/**
 * FeaturedListingManagement
 * Manage sponsored/featured listings with performance metrics, create/renew/cancel.
 */
import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import api from '../api';
import StatusBadge from '../components/StatusBadge';

const SECTORS = [
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'finance', label: 'Finance' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'home-services', label: 'Home Services' },
  { value: 'retail', label: 'Retail' },
  { value: 'travel', label: 'Travel' },
];

const INITIAL_FORM = {
  sector: 'healthcare',
  position: '1',
  startDate: '',
  endDate: '',
  title: '',
  description: '',
  ctaUrl: '',
};

function generateMockListings() {
  return [
    {
      id: 'fl-1',
      sector: 'healthcare',
      position: 1,
      title: 'Top Healthcare Provider',
      startDate: '2026-01-15',
      endDate: '2026-04-15',
      status: 'live',
      impressions: 12450,
      clicks: 843,
      ctr: 6.77,
    },
    {
      id: 'fl-2',
      sector: 'telecom',
      position: 3,
      title: 'Best Telecom Deals',
      startDate: '2026-02-01',
      endDate: '2026-05-01',
      status: 'live',
      impressions: 8920,
      clicks: 521,
      ctr: 5.84,
    },
  ];
}

function generatePerformanceData() {
  const data = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    data.push({
      date: d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }),
      impressions: Math.floor(200 + Math.random() * 600),
      clicks: Math.floor(10 + Math.random() * 60),
    });
  }
  return data;
}

export default function FeaturedListingManagement() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [formErrors, setFormErrors] = useState({});

  // Fetch listings
  const { data: listingsRaw } = useQuery({
    queryKey: ['featured-listings'],
    queryFn: () => api.get('/api/payments/featured/listings').catch(() => null),
    placeholderData: null,
  });

  const listings = listingsRaw?.listings ?? generateMockListings();
  const perfData = generatePerformanceData();

  // Create listing
  const createMutation = useMutation({
    mutationFn: (payload) => api.post('/api/payments/featured/listings', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['featured-listings'] });
      setShowForm(false);
      setForm({ ...INITIAL_FORM });
    },
  });

  // Renew listing
  const renewMutation = useMutation({
    mutationFn: (listingId) => api.post(`/api/payments/featured/listings/${listingId}/renew`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['featured-listings'] }),
  });

  // Cancel listing
  const cancelMutation = useMutation({
    mutationFn: (listingId) => api.post(`/api/payments/featured/listings/${listingId}/cancel`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['featured-listings'] }),
  });

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: null }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const errors = {};
    if (!form.title.trim()) errors.title = 'Title is required';
    if (!form.startDate) errors.startDate = 'Start date is required';
    if (!form.endDate) errors.endDate = 'End date is required';
    if (form.startDate && form.endDate && form.startDate >= form.endDate) errors.endDate = 'End date must be after start date';
    if (!form.ctaUrl.trim()) errors.ctaUrl = 'CTA URL is required';

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    createMutation.mutate({
      sector: form.sector,
      position: parseInt(form.position, 10),
      startDate: form.startDate,
      endDate: form.endDate,
      title: form.title.trim(),
      description: form.description.trim(),
      ctaUrl: form.ctaUrl.trim(),
    });
  }, [form, createMutation]);

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1e293b', marginBottom: '4px' }}>
            Featured Listings
          </h1>
          <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
            Manage your sponsored listings and track performance.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px', fontSize: '14px', fontWeight: '600', borderRadius: '8px',
            border: 'none', backgroundColor: '#3b82f6', color: '#fff', cursor: 'pointer',
          }}
        >
          {showForm ? 'Cancel' : 'Create New Listing'}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div style={{
          backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
          padding: '24px', marginBottom: '28px',
        }}>
          <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '20px' }}>
            New Featured Listing
          </h2>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <label style={labelStyle}>Sector</label>
                <select name="sector" value={form.sector} onChange={handleFormChange} style={inputStyle}>
                  {SECTORS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 120px' }}>
                <label style={labelStyle}>Position (1-5)</label>
                <select name="position" value={form.position} onChange={handleFormChange} style={inputStyle}>
                  {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelStyle}>Start Date</label>
                <input type="date" name="startDate" value={form.startDate} onChange={handleFormChange} style={{
                  ...inputStyle, borderColor: formErrors.startDate ? '#ef4444' : '#d1d5db',
                }} />
                {formErrors.startDate && <div style={errorStyle}>{formErrors.startDate}</div>}
              </div>
              <div style={{ flex: '1 1 160px' }}>
                <label style={labelStyle}>End Date</label>
                <input type="date" name="endDate" value={form.endDate} onChange={handleFormChange} style={{
                  ...inputStyle, borderColor: formErrors.endDate ? '#ef4444' : '#d1d5db',
                }} />
                {formErrors.endDate && <div style={errorStyle}>{formErrors.endDate}</div>}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Title</label>
              <input type="text" name="title" value={form.title} onChange={handleFormChange} placeholder="e.g., Top Healthcare Provider in Ontario" style={{
                ...inputStyle, borderColor: formErrors.title ? '#ef4444' : '#d1d5db',
              }} />
              {formErrors.title && <div style={errorStyle}>{formErrors.title}</div>}
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Description (optional)</label>
              <textarea name="description" value={form.description} onChange={handleFormChange} rows={3} placeholder="Short description for your listing..." style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>CTA URL</label>
              <input type="url" name="ctaUrl" value={form.ctaUrl} onChange={handleFormChange} placeholder="https://yoursite.ca/offer" style={{
                ...inputStyle, borderColor: formErrors.ctaUrl ? '#ef4444' : '#d1d5db',
              }} />
              {formErrors.ctaUrl && <div style={errorStyle}>{formErrors.ctaUrl}</div>}
            </div>
            {createMutation.isError && (
              <div style={{ padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#991b1b', marginBottom: '16px' }}>
                {createMutation.error?.message || 'Failed to create listing'}
              </div>
            )}
            <button
              type="submit"
              disabled={createMutation.isPending}
              style={{
                padding: '10px 24px', fontSize: '14px', fontWeight: '600', borderRadius: '8px',
                border: 'none', backgroundColor: createMutation.isPending ? '#93c5fd' : '#3b82f6',
                color: '#fff', cursor: createMutation.isPending ? 'wait' : 'pointer',
              }}
            >
              {createMutation.isPending ? 'Creating...' : 'Create Listing'}
            </button>
          </form>
        </div>
      )}

      {/* Active listings */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px', marginBottom: '28px',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          Active Listings
        </h2>
        {listings.length === 0 ? (
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>No active listings. Create one to get started.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                {['Title', 'Sector', 'Pos.', 'Dates', 'Impressions', 'Clicks', 'CTR', 'Status', 'Actions'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: '#64748b', fontWeight: '600', fontSize: '12px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.map((listing) => (
                <tr key={listing.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '10px', fontWeight: '500', color: '#1e293b' }}>{listing.title}</td>
                  <td style={{ padding: '10px', color: '#475569', textTransform: 'capitalize' }}>{listing.sector}</td>
                  <td style={{ padding: '10px', color: '#475569', textAlign: 'center' }}>{listing.position}</td>
                  <td style={{ padding: '10px', color: '#475569', fontSize: '12px' }}>
                    {listing.startDate} to {listing.endDate}
                  </td>
                  <td style={{ padding: '10px', color: '#1e293b', fontWeight: '600' }}>{listing.impressions.toLocaleString('en-CA')}</td>
                  <td style={{ padding: '10px', color: '#1e293b', fontWeight: '600' }}>{listing.clicks.toLocaleString('en-CA')}</td>
                  <td style={{ padding: '10px', color: '#1e293b', fontWeight: '600' }}>{listing.ctr}%</td>
                  <td style={{ padding: '10px' }}><StatusBadge status={listing.status} /></td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={() => renewMutation.mutate(listing.id)}
                        style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer' }}
                      >
                        Renew
                      </button>
                      <button
                        type="button"
                        onClick={() => cancelMutation.mutate(listing.id)}
                        style={{ padding: '4px 10px', fontSize: '11px', fontWeight: '600', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fff', color: '#dc2626', cursor: 'pointer' }}
                      >
                        Cancel
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Performance chart */}
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0',
        padding: '24px',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px' }}>
          Performance - Impressions vs Clicks
        </h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={perfData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: '#94a3b8' }} />
            <Tooltip contentStyle={{ fontSize: '13px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
            <Legend wrapperStyle={{ fontSize: '12px' }} />
            <Line yAxisId="left" type="monotone" dataKey="impressions" stroke="#3b82f6" strokeWidth={2} dot={false} />
            <Line yAxisId="right" type="monotone" dataKey="clicks" stroke="#10b981" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#374151',
  marginBottom: '4px',
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '14px',
  borderRadius: '8px',
  border: '1px solid #d1d5db',
  backgroundColor: '#fff',
  color: '#1e293b',
  outline: 'none',
  boxSizing: 'border-box',
};

const errorStyle = {
  fontSize: '12px',
  color: '#ef4444',
  marginTop: '3px',
};
